<script setup lang="ts">
import { HttpStatusCode, isAxiosError } from 'axios';
import { BAlert, BCard, BCardBody, BCol, BLink, BRow } from 'bootstrap-vue-next';
import { onMounted, ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { useRoute } from 'vue-router';

import AddUserSvg from '@images/add-user-sm.svg';
import ChatSvg from '@images/chat-sm.svg';
import EmptySearchSvg from '@images/empty-search-md.svg';
import MergeRequestsSvg from '@images/merge-requests-sm.svg';
import MilestoneSvg from '@images/milestone-sm.svg';
import ServiceDeskSvg from '@images/service-desk-sm.svg';

import type { GetApiV1TeamMembersByUsernameResponse } from '~/api/generated';
import { checkinTeamMember, fetchTeamMember } from '~/api/teamMembers';
import ErrorState from '~/common/components/ErrorState.vue';
import ThemedOverlay from '~/common/components/ThemedOverlay.vue';
import { useVueData } from '~/common/useVueData';

const route = useRoute();
const username = String(route.params.username);

const { isSignedIn, isCurrentUsername } = useVueData();
const isOwner = isCurrentUsername(username);

const COACH_ICON = AddUserSvg;
const WAVE_EMOJI = '\u{1F44B}';
const MR_COACH_ONBOARDING_URL = 'https://gitlab.com/gitlab-org/developer-relations/contributor-success/team-task/-/work_items/new?issue_template=mr_coach_onboarding&description_template=mr_coach_onboarding';

type Breakdown = GetApiV1TeamMembersByUsernameResponse['breakdown'];

interface CriterionCta {
  label: string;
  href?: string;
  to?: RouteLocationRaw;
}

interface Criterion {
  key: Exclude<keyof Breakdown, 'mr_coach'>;
  icon: string;
  label: string;
  description: string;
  cta?: CriterionCta;
}

const CRITERIA: readonly Criterion[] = [
  {
    key: 'community_mrs',
    icon: MergeRequestsSvg,
    label: 'Interacted with community merge requests',
    description: 'Reviewed or commented on merge requests labelled "Community contribution".',
  },
  {
    key: 'quick_wins',
    icon: MilestoneSvg,
    label: 'Created quick wins',
    description: 'Opened issues labelled "quick win" for the community to pick up.',
    cta: {
      label: 'What makes a good quick win?',
      href: 'https://handbook.gitlab.com/handbook/marketing/developer-relations/contributor-success/community-contributors-workflows/#criteria-for-quick-win-issues',
    },
  },
  {
    key: 'discord',
    icon: ChatSvg,
    label: 'Posted on Discord',
    description: 'Sent a message in the community Discord.',
    cta: {
      label: 'Join the community Discord',
      to: { name: 'documentation', params: { page: 'user-guide' }, hash: '#discord-messages' },
    },
  },
  {
    key: 'forum',
    icon: ServiceDeskSvg,
    label: 'Posted on the forum',
    description: 'Posts and replies on the community forum.',
    cta: {
      label: 'Join the GitLab forum',
      to: { name: 'documentation', params: { page: 'user-guide' }, hash: '#forum-posts' },
    },
  },
];

interface WindowItem {
  key: keyof Breakdown['community_mrs']['windows'];
  label: string;
}

const WINDOWS: readonly WindowItem[] = [
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'all_time', label: 'All-time' },
];

const score = ref<GetApiV1TeamMembersByUsernameResponse | null>(null);
const isLoading = ref(true);
const hasError = ref(false);
const isNotFound = ref(false);

const GETTING_STARTED_LEVEL = 3;

const STARTED_MESSAGES = [
  'Building momentum',
  'Making an impact',
  'Community champion',
  'Going faster together',
] as const;

const startedMessage = STARTED_MESSAGES[Math.floor(Math.random() * STARTED_MESSAGES.length)];

const isGettingStarted = (level: number) => level < GETTING_STARTED_LEVEL;

const levelMessage = (level: number) => (isGettingStarted(level) ? 'Getting started' : startedMessage);

const isSayingHello = ref(false);
const hasCheckinError = ref(false);

const sayHello = async () => {
  isSayingHello.value = true;
  hasCheckinError.value = false;
  try {
    await checkinTeamMember(username);

    if (score.value) {
      score.value = { ...score.value, checked_in: true };
    }
  } catch {
    hasCheckinError.value = true;
  } finally {
    isSayingHello.value = false;
  }
};

onMounted(async () => {
  try {
    score.value = await fetchTeamMember(username);
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === HttpStatusCode.NotFound) {
      isNotFound.value = true;
    } else {
      hasError.value = true;
    }
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div
    v-if="isNotFound"
    class="text-center"
  >
    <img
      :src="EmptySearchSvg"
      alt="Magnifying glass"
      class="my-3"
    >
    <h2 class="fw-bold">
      Team member not found.
    </h2>
  </div>
  <ErrorState v-else-if="hasError" />
  <ThemedOverlay
    v-else
    :show="isLoading"
  >
    <div class="gft-hero text-center my-4 p-4 p-md-5">
      <h1 class="gft-hero__title">Co-create - Go Faster Together</h1>
      <p class="gft-hero__subtitle">
        Everyone can contribute, and every interaction with our community helps us go faster together.
        Here's how <strong>{{ username }}</strong> is doing.
      </p>
    </div>
    <template v-if="score">
      <BAlert
        v-if="!isSignedIn"
        :model-value="true"
        variant="info"
        class="mb-4"
      >
        You are viewing @{{ username }}'s profile - if you are a team member, please
        <BLink href="/login?return=/team-members/me">log in</BLink> to view yours.
      </BAlert>
      <BAlert
        v-else-if="!isOwner"
        :model-value="true"
        variant="info"
        class="mb-4"
      >
        You are viewing @{{ username }}'s profile -
        <BLink href="/team-members/me">click here</BLink> to view yours.
      </BAlert>
      <BAlert
        v-else-if="!score.is_team_member"
        :model-value="true"
        variant="info"
        class="mb-4"
      >
        {{ username }} does not appear to be a GitLab team member.
        If you believe this is incorrect, please raise it with the community engineering team.
      </BAlert>

      <BCard
        no-body
        class="gft-level-card mb-4"
        :class="isGettingStarted(score.level) ? 'gft-level-card--start' : 'gft-level-card--started'"
      >
        <BCardBody class="gft-level-card__body">
          <div class="gft-medallion">
            <span class="gft-medallion__level">{{ score.level }}</span>
          </div>
          <div class="gft-level-card__info">
            <div class="gft-level-card__label">
              Level {{ score.level }}
            </div>
            <div class="gft-level-card__tier">
              {{ levelMessage(score.level) }}
            </div>
            <p class="gft-level-card__hint mb-0">
              Thank you for everything you do to support our community, {{ username }}.
              You're helping us go faster together.
            </p>
          </div>
        </BCardBody>
      </BCard>

      <BAlert
        v-if="isOwner"
        :model-value="hasCheckinError"
        variant="danger"
        class="mb-4"
      >
        Something went wrong saying hello. Please try again, and reach out to the
        community engineering team if the problem persists.
      </BAlert>

      <BCard
        v-if="isOwner"
        no-body
        class="gft-hello mb-4"
      >
        <BCardBody class="gft-hello__body">
          <div class="gft-hello__text">
            <div class="gft-hello__title">
              {{ score.checked_in ? 'Thanks for saying hello!' : "Let's co-create" }}
            </div>
            <p class="gft-hello__desc">
              Our team runs the co-create program, builds and maintains this platform, and looks after contributor onboarding, recognition, and rewards.
              Take a look around and explore the types of contribution we recognize and reward.
            </p>
            <p class="gft-hello__desc mb-0">
              <template v-if="score.checked_in">
                You're checked in. If you qualify, we'll be in touch regarding your reward store credits, which you can redeem to plant a tree or for some swag.
              </template>
              <template v-else>
                Be one of the first 100 team members to say hello, and receive credits for our exclusive reward store, where you plant a tree or claim some swag.
              </template>
            </p>
          </div>
          <button
            v-if="!score.checked_in"
            type="button"
            class="gft-hello__button"
            :disabled="isSayingHello"
            @click="sayHello"
          >
            <span class="gft-hello__button-emoji">{{ WAVE_EMOJI }}</span>
            Say hello
          </button>
          <div
            v-else
            class="gft-hello__badge"
          >
            &check; Checked in
          </div>
        </BCardBody>
      </BCard>

      <h2 class="gft-section-title mb-3">
        Ways to level up
      </h2>

      <BRow class="g-3">
        <BCol cols="12">
          <div
            class="gft-tile gft-tile--wide"
            :class="{ 'gft-tile--done': score.breakdown.mr_coach.mr_coach }"
          >
            <div class="gft-tile__icon">
              <img
                :src="COACH_ICON"
                alt="Merge request coach icon"
                width="44"
                height="44"
              >
            </div>
            <div class="gft-tile__body">
              <div class="gft-tile__label">
                Merge request coach
              </div>
              <div class="gft-tile__desc">
                Signed up to coach community contributors.
              </div>
              <BLink
                v-if="!score.breakdown.mr_coach.mr_coach"
                :href="MR_COACH_ONBOARDING_URL"
                target="_blank"
                class="gft-tile__cta mt-2"
              >
                Become a merge request coach &rarr;
              </BLink>
            </div>
            <div class="gft-tile__aside">
              <span
                class="gft-check"
                :class="{ 'gft-check--on': score.breakdown.mr_coach.mr_coach }"
              >
                {{ score.breakdown.mr_coach.mr_coach ? '\u2713' : '\u25cb' }}
              </span>
              <div class="gft-tile__points">
                +{{ score.breakdown.mr_coach.points }}
              </div>
            </div>
          </div>
        </BCol>
        <BCol
          v-for="criterion in CRITERIA"
          :key="criterion.key"
          cols="12"
          md="6"
        >
          <div
            class="gft-tile h-100"
            :class="{ 'gft-tile--done': score.breakdown[criterion.key].points > 0 }"
          >
            <div class="gft-tile__icon">
              <img
                :src="criterion.icon"
                width="44"
                height="44"
              >
            </div>
            <div class="gft-tile__body">
              <div class="gft-tile__label">
                {{ criterion.label }}
              </div>
              <div class="gft-tile__desc">
                {{ criterion.description }}
              </div>
              <div class="gft-windows mt-2">
                <span
                  v-for="window in WINDOWS"
                  :key="window.key"
                  class="gft-window"
                  :class="{ 'gft-window--on': score.breakdown[criterion.key].windows[window.key] }"
                >
                  {{ window.label }}
                </span>
              </div>
              <template v-if="criterion.cta && score.breakdown[criterion.key].points === 0">
                <BLink
                  v-if="criterion.cta.href"
                  :href="criterion.cta.href"
                  target="_blank"
                  class="gft-tile__cta mt-2"
                >
                  {{ criterion.cta.label }} &rarr;
                </BLink>
                <BLink
                  v-else
                  :to="criterion.cta.to"
                  class="gft-tile__cta mt-2"
                >
                  {{ criterion.cta.label }} &rarr;
                </BLink>
              </template>
            </div>
            <div class="gft-tile__aside">
              <div class="gft-tile__points">
                +{{ score.breakdown[criterion.key].points }}
              </div>
            </div>
          </div>
        </BCol>
      </BRow>
    </template>
  </ThemedOverlay>
</template>

<style scoped>
/* GitLab brand tanuki + purple palette */
.gft-hero {
  border-radius: 16px;
  color: #fff;
  background:
    radial-gradient(circle at 15% 20%, rgba(252, 109, 38, 0.55), transparent 55%),
    radial-gradient(circle at 85% 80%, rgba(226, 67, 41, 0.45), transparent 55%),
    linear-gradient(135deg, #380d75 0%, #6b4fbb 45%, #fc6d26 100%);
  box-shadow: 0 12px 32px rgba(56, 13, 117, 0.35);
}

.gft-hero__title {
  font-weight: 700;
  letter-spacing: -0.02em;
  font-size: clamp(2rem, 5vw, 3rem);
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
}

.gft-hero__subtitle {
  color: rgba(255, 255, 255, 0.9);
  max-width: 42rem;
  margin-inline: auto;
}

.gft-hero__subtitle :deep(strong) {
  color: #fff;
}

.gft-level-card {
  border: 0;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.gft-level-card::before {
  content: '';
  position: absolute;
  inset: 0 0 auto 0;
  height: 6px;
  background: linear-gradient(90deg, #fca326, #fc6d26, #e24329);
}

.gft-level-card__body {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.75rem;
}

.gft-level-card__info {
  flex: 1 1 auto;
  min-width: 0;
}

.gft-level-card__label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
}

.gft-level-card__tier {
  font-size: 1.5rem;
  font-weight: 660;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin-top: 0.1rem;
}

.gft-level-card__hint {
  font-size: 0.9rem;
  color: var(--bs-secondary-color);
  margin-top: 0.35rem;
}

.gft-medallion {
  flex: 0 0 auto;
  width: 6rem;
  height: 6rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #6b4fbb, #fc6d26);
  box-shadow: 0 6px 18px rgba(107, 79, 187, 0.4);
}

.gft-medallion__level {
  font-size: 3rem;
  font-weight: 800;
  line-height: 1;
  color: #fff;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

/* Neutral while getting started, warm tanuki gradient once under way */
.gft-level-card--start .gft-medallion {
  background: linear-gradient(135deg, #71717a, #52525b);
  box-shadow: 0 6px 18px rgba(82, 82, 91, 0.35);
}

.gft-level-card--started .gft-medallion {
  background: linear-gradient(135deg, #6b4fbb, #fc6d26);
}

.gft-hello {
  border: 0;
  border-radius: 16px;
  overflow: hidden;
  color: #fff;
  background:
    radial-gradient(circle at 90% 15%, rgba(252, 163, 38, 0.5), transparent 55%),
    linear-gradient(135deg, #6b4fbb 0%, #fc6d26 100%);
  box-shadow: 0 10px 28px rgba(107, 79, 187, 0.3);
}

.gft-hello__body {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem 1.75rem;
}

.gft-hello__text {
  flex: 1 1 auto;
  min-width: 0;
}

.gft-hello__title {
  font-size: 1.35rem;
  font-weight: 660;
  letter-spacing: -0.01em;
}

.gft-hello__desc {
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.95rem;
  margin-top: 0.25rem;
}

.gft-hello__link {
  color: #fff;
  font-weight: 600;
  text-decoration: underline;
}

.gft-hello__link:hover {
  color: #fff;
}

.gft-hello__button {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #542e91;
  background: #fff;
  border: 0;
  border-radius: 999px;
  padding: 0.75rem 1.75rem;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
}

.gft-hello__button:hover:not(:disabled),
.gft-hello__button:focus-visible {
  background: #fff;
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.24);
  outline: none;
}

.gft-hello__button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.gft-hello__button:disabled {
  opacity: 0.75;
  cursor: default;
}

.gft-hello__button-emoji {
  font-size: 1.2rem;
  line-height: 1;
}

.gft-hello__badge {
  flex: 0 0 auto;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
}

.gft-section-title {
  font-weight: 660;
  letter-spacing: -0.01em;
}

.gft-tile {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 16px;
  background: var(--background-color-card);
  border: 1px solid var(--bs-border-color-translucent);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.gft-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.1);
}

.gft-tile--done {
  border-color: rgba(107, 79, 187, 0.35);
  background:
    linear-gradient(135deg, rgba(107, 79, 187, 0.08), rgba(252, 109, 38, 0.06)),
    var(--background-color-card);
}

.gft-tile__icon {
  flex: 0 0 auto;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gft-tile__icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.gft-tile__body {
  flex: 1 1 auto;
  min-width: 0;
}

.gft-tile__label {
  font-weight: 600;
  line-height: 1.3;
}

.gft-tile__desc {
  font-size: 0.85rem;
  color: var(--bs-secondary-color);
  margin-top: 0.15rem;
}

.gft-tile__cta {
  display: inline-block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b4fbb;
  text-decoration: none;
}

.gft-tile__cta:hover {
  text-decoration: underline;
}

.gft-tile__aside {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
}

.gft-tile__points {
  font-weight: 700;
  font-size: 0.95rem;
  color: #6b4fbb;
  background: rgba(107, 79, 187, 0.12);
  border-radius: 999px;
  padding: 0.1rem 0.6rem;
}

.gft-check {
  font-size: 1.25rem;
  color: var(--bs-secondary-color);
}

.gft-check--on {
  color: #fc6d26;
}

.gft-windows {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.gft-window {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  color: var(--bs-secondary-color);
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color-translucent);
}

.gft-window--on {
  color: #fff;
  background: linear-gradient(135deg, #6b4fbb, #fc6d26);
  border-color: transparent;
}

@media (max-width: 767.98px) {
  .gft-level-card__body,
  .gft-hello__body {
    flex-direction: column;
    text-align: center;
  }
}
</style>
